import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { combineLatest, take } from 'rxjs';
import { MqttProjectService } from 'src/app/core/app-load/mqtt-project.service';
import { SoftwarePermissionCode } from 'src/app/core/models/permissions/permission-code';
import { Property } from 'src/app/core/models/project/property.model';
import { User } from 'src/app/core/models/user/user.model';
import { CurrentUserStoreService } from 'src/app/core/services/current-user-store.service';
import { ApiProjectService } from 'src/app/modules/project/services/http/api-project.service';
import { ProjectService } from 'src/app/modules/project/services/project.service';
import { ApiSettingsService, DefaultsPreset } from 'src/app/modules/settings/services/http/api-settings.service';
import { CustomTranslatePipe } from 'src/app/shared/pipes/custom-translate.pipe';
import { LocationGroup } from 'src/app/shared/services/api-location-groups.service';
import { DemoModeService } from 'src/app/shared/services/demo-mode.service';
import { DEMO_MODE } from 'src/environments/environment';

@Component({
  selector: 'app-group-settings',
  templateUrl: './group-settings.component.html',
  styleUrls: ['./group-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GroupSettingsComponent implements OnInit {
  @Input() selectedGroup: LocationGroup;
  defaultPresets: DefaultsPreset[];
  possiblePresetProps: Property [];
  targetProps: Property[];
  targetEquipmentRelayPropsOptions: Property[];
  targetEquipmentRelayPropValues: Property[] = [];
  signedInUser: User;
  $allLocations = this.projectService.getAllLocations();
  swPermissions = SoftwarePermissionCode;

  loadingGroupSettings$ = this.mqttProjectService.loadingEditgroupSettings$;



  constructor(
    private apiSettingsService: ApiSettingsService,
    private apiProjectService: ApiProjectService,
    private currentUserStoreService: CurrentUserStoreService,
    private toastController: ToastController,
    private pipe: CustomTranslatePipe,
    private demoModeService: DemoModeService,
    private projectService: ProjectService,
    private cdr: ChangeDetectorRef,
    private mqttProjectService: MqttProjectService
    ) { }

  ngOnInit(): void {
    combineLatest([
      this.apiSettingsService.getDefaultPresets(),
      this.apiSettingsService.getPresetProps(),
    ]).
        subscribe(([defaults, presetProps])=> {
          this.defaultPresets = defaults;
          this.possiblePresetProps = presetProps;
          this.targetProps = this.combinePropsWithDefaults([])[0];
          this.targetEquipmentRelayPropsOptions = this.combinePropsWithDefaults([])[1]
          this.cdr.detectChanges();
        })
    this.signedInUser = Object.assign(new User(), this.currentUserStoreService.getUser());
    if (DEMO_MODE) {
      this.demoModeService.getData().subscribe( value => {
        this.defaultPresets = value.Presets;
        this.possiblePresetProps = value.PresetProps
        this.targetProps =this.combinePropsWithDefaults([])[0];
        this.targetEquipmentRelayPropsOptions = this.combinePropsWithDefaults([])[1]
        this.cdr.detectChanges();
      })
    }
  }

  combinePropsWithDefaults(defaultProps): Property[][] {
    // const presetProps = JSON.parse(JSON.stringify(this.presetProps));
    const presetProps: any[] = structuredClone(this.possiblePresetProps);
     const props = presetProps.map((presetP)=> {
      const dProp = defaultProps.find((prop)=> {
        if (Property.isAnyNonStatusRelayOutputProperty(prop)) {
          return prop.name.split('|')[0] === presetP.name.split('|')[0]
        }
        return prop.type === presetP.type
      })
      if (!dProp) {
        presetP.include = false;
        presetP.value = null;
        return presetP;
      } else {
        presetP.include = true;
        presetP.value = dProp.value;
        return presetP
      }
     })

     const hvacTemp:Property[] = [];
     const hvacOther:Property[] = [];
     const equipmentIgnoreProps:Property[] = [];
     const equipmentRelayOutput:Property[] = [];
     const equipmentOther:Property[] = [];
     const access:Property[] = [];
     const burglary:Property[] = [];
     const other:Property[] = [];

     props.forEach((p: Property)=> {
      if (p.mqttTopic.startsWith('hvac/temp/')) {
        hvacTemp.push(p)
      }  else if (p.mqttTopic.startsWith('hvac/')) {
        hvacOther.push(p)
      }  else if (p.mqttTopic.startsWith('equipment/ignore')) {
        equipmentIgnoreProps.push(p)
      } else if (p.mqttTopic.startsWith('equipment/relayoutput/')) {
        if (p.name.includes('|')) {
          const propAlreadyIncluded = equipmentRelayOutput.some((pr)=> {
            return pr?.name?.split('|')[0] === p?.name?.split('|')[0]
          })
          if (!propAlreadyIncluded) {
            equipmentRelayOutput.push(p)
          }
        } else {
        equipmentRelayOutput.push(p)
      }
      } else if (p.mqttTopic.startsWith('equipment/')) {
        equipmentOther.push(p)
      } else if (p.mqttTopic.startsWith('access/lock')) {
        access.push(p)
      } else if (p.mqttTopic.startsWith('security/burglary/') || p.mqttTopic.startsWith('access/burglary/')) {
        burglary.push(p)
      } else {
        other.push(p)
      }
     })
     const nonRelayEquipmentProps = [...hvacTemp,...hvacOther,...equipmentIgnoreProps,
      /* ...equipmentRelayOutput */...equipmentOther,...access,...burglary,...other]
    const equipmentRelayProps = [...equipmentRelayOutput]
     return [nonRelayEquipmentProps, equipmentRelayProps]


  }

  onClickPreset(preset) {
    this.targetProps = this.combinePropsWithDefaults( preset.equipmentPresetProperties.$values)[0]
    this.targetEquipmentRelayPropsOptions = this.combinePropsWithDefaults(preset.equipmentPresetProperties.$values)[1]
    this.targetEquipmentRelayPropValues = preset.equipmentPresetProperties.$values.filter((p)=> {
      return Property.isAnyNonStatusRelayOutputProperty(p)
    })
  }

  clearValues() {
    this.targetProps = this.combinePropsWithDefaults([])[0];
    this.targetEquipmentRelayPropsOptions = this.combinePropsWithDefaults([])[1]
    this.targetEquipmentRelayPropValues = [];
  }

  async onApply() {
    if (!this.signedInUser.havePermission(SoftwarePermissionCode.LocationGroupsActivatePresets)) {
      const toast = await this.toastController.create({
        message: this.pipe.transform('Permission required. You do not have permission to activate preset.'),
        duration: 2000
      });
      await toast.present();
      return;
    }
    const includedProps: any[] = this.targetProps.filter((prop:any) => {
      return prop.include;
    });

    const includedRelayProps: any[] = this.targetEquipmentRelayPropValues.filter((prop )=> {
      const propIsIncluded = this.targetEquipmentRelayPropsOptions.some((p:any)=> {
        return p.include && (p.name.split('|')[0] === prop.name.split('|')[0])
      })
      return propIsIncluded // && Number.isInteger(prop.value)
      // return prop.include && Number.isInteger(prop.value)
    })
    const allIncludedProps = [...includedProps, ...includedRelayProps]

    const propsToSend: {typeCode:number, value: number}[] = [];
    let burglaryDetectedDoorProp;
    let burglaryDetectedWindowProp;
    let burglaryDetectedPIRProp;

   /*  propsToSend = propsToSend.map((prop) => {
      return {
        typeCode: prop.type,
        value: prop.value
      }
    });
    */

    allIncludedProps.forEach((prop)=> {
      if (Property.isBurglaryDetectedDoor(prop)) {
        burglaryDetectedDoorProp = (prop)
      } else  if (Property.isBurglaryDetectedWindow(prop)) {
        burglaryDetectedWindowProp = (prop)
      } else if (Property.isBurglaryDetectedByPIR(prop)) {
        burglaryDetectedPIRProp = (prop)
      } else {
        // propsToSend.push(prop)
        propsToSend.push({
          typeCode: prop.type,
          value: prop.value
        })
      }
    })
    if (propsToSend.length > 0 && this.selectedGroup.id) {
      this.apiProjectService.changeMultiplePropertiesByGroup(this.selectedGroup.id, propsToSend).subscribe({
        next: () => {
       },
       error: (err)=> {
       }});
    }

    if (burglaryDetectedDoorProp && this.selectedGroup.id) {
      if (burglaryDetectedDoorProp.value) {
        this.apiProjectService.resumePublishingByGroup(burglaryDetectedDoorProp.type,this.selectedGroup.id).subscribe()
      } else {
        this.apiProjectService.stopPublishingByGroup( burglaryDetectedDoorProp.type, this.selectedGroup.id).subscribe()
      }
    }
    if (burglaryDetectedWindowProp && this.selectedGroup.id) {
      if (burglaryDetectedWindowProp.value) {
        this.apiProjectService.resumePublishingByGroup(burglaryDetectedWindowProp.type,this.selectedGroup.id).subscribe()
      } else {
        this.apiProjectService.stopPublishingByGroup( burglaryDetectedWindowProp.type, this.selectedGroup.id).subscribe()
      }
    }
    if (burglaryDetectedPIRProp && this.selectedGroup.id) {
      if (burglaryDetectedPIRProp.value) {
        this.apiProjectService.resumePublishingByGroup(burglaryDetectedPIRProp.type,this.selectedGroup.id).subscribe()
      } else {
        this.apiProjectService.stopPublishingByGroup( burglaryDetectedPIRProp.type, this.selectedGroup.id).subscribe()
      }
    }
  }

  defaultPropChange(event: any, prop:Property) {
    if (Property.isCO2SetPoint(prop)) {
      // no validation for co2setpoint
      return
    }

    const maxValue = 30;
    const minValue = 0

    if(prop.writeDataType == 'B') {
      if (event.target.value < minValue) {
        prop.value = minValue
      } else if (event.target.value > maxValue) {
        prop.value = maxValue
      }
    } else if (prop.writeDataType == 'b') {
      if (event.target.value < 0) {
        prop.value = 0
      } else if (event.target.value > 1) {
        prop.value = 1
      }
    }
  }

  equipmentRelayPropValuesIncludes(prop) {
    return this.targetEquipmentRelayPropValues.some((p)=> {
      return prop.name === p.name
    })
  }

  onClickRelayOutputPropValue(pr: Property) {
    const prop = structuredClone(pr)
    prop.value = 1;

    const index = this.targetEquipmentRelayPropValues.findIndex((p: Property) => {
      return prop.name.split('|')[0] === p.name.split('|')[0];
    });
    if (index === -1) {
      this.targetEquipmentRelayPropValues.push(prop);
    } else {
      this.targetEquipmentRelayPropValues.splice(index, 1);
      this.targetEquipmentRelayPropValues.push(prop);
    }
  }

  getRelayRadioValue(props: Property[]): number | null {
    if (!props || props.length === 0) return null;

    const prefix = props[0].name.split('|')[0];

    const relayProp = this.targetEquipmentRelayPropValues.find((p: Property) => {
      return p.name.split('|')[0] === prefix;
    });

    return relayProp ? relayProp.value : null;
  }

  onRelayToggle(event: any, pr: Property) {
    const val = event.detail.value;
    const prefix = pr.name.split('|')[0];

    this.targetEquipmentRelayPropValues = this.targetEquipmentRelayPropValues.filter(p => {
      return p.name.split('|')[0] !== prefix;
    });

    if (val !== null && val !== undefined) {
      const prop = structuredClone(pr);
      prop.value = val;
      this.targetEquipmentRelayPropValues.push(prop);
    }
}

}
